import React from "react";
import { useTranslation } from "react-i18next";
import {
    StyledModal,
    StyledButton,
    StyledLabel,
    StyledH2,
} from "@aureskonnect/react-ui";
import { useSnapshot } from "valtio";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { store as useStore } from "@components/VerticalLayout/store";
import { capitalize } from "../../../helpers/general";

import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { useFetchGlobal } from "../../../api/hooks";
import { showSuccessToast } from "@components/Common/SuccessToast";
import ErrorToast from "@components/Common/ErrorTost";

import { archiveApplications } from "./PosDeviceSetup/api";
import AlertSvgIcon from "@components/Common/SvgIcons/AlertSvgIcon";

type ConfirmationModalArchivedProps = {
    isConfirmationModalArchived: boolean;
    setIsConfirmationModalArchived: (value: boolean) => void;
    setIsDataUpdated: (value: boolean) => void;
    selectedRows: any[];
};

export function ConfirmationModalArchived({
    isConfirmationModalArchived,
    setIsConfirmationModalArchived,
    setIsDataUpdated,
    selectedRows,
}: ConfirmationModalArchivedProps) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const { shopID, oneShop, userID, franchiseID, operatorID } = useSnapshot(
        useStore
    );
    async function handleArchive() {
        try {
            const result: any = await archiveApplications(fetchGlobal, {
                userID: String(userID),
                franchiseID,
                operatorID,
                oneShop,
                shopID,
                selectedRows,
                t,
            });
            if (result.error) throw new Error("error");
            showSuccessToast(
                selectedRows.length > 1
                    ? `${t("These applications were successfully archived")!}`
                    : `${t("This application was successfully archived")!}`
            );
            setIsConfirmationModalArchived(!isConfirmationModalArchived);
            setIsDataUpdated(true);
        } catch (e) {
            ErrorToast(`${t("There's an error")!}`);
        }
    }

    return (
        <StyledModal
            toggle={true}
            isOpen={isConfirmationModalArchived!}
            centered
        >
            <ModalHeader
                className="text-capitalize"
                toggle={() => {
                    setIsConfirmationModalArchived(
                        !isConfirmationModalArchived
                    );
                }}
            >
                <StyledH2>{capitalize(t("Alert"))}</StyledH2>
            </ModalHeader>
            <ModalBody>
                <StyledLabel className="pl-3 m-0">
                    {selectedRows.length > 1
                        ? t(
                              "Do you want to confirm the archiving of the selected applications?"
                          )
                        : t(
                              "Do you want to confirm the archiving of the selected application?"
                          )}
                </StyledLabel>
                {selectedRows[0]?.insidePublishProject && (
                    <div
                        role="alert"
                        className="pl-3 mt-2 d-flex align-items-center"
                        style={{ color: "#e31111" }}
                    >
                        <AlertSvgIcon
                            className="mr-1"
                            aria-hidden="true"
                            focusable="false"
                        />
                        <span>
                            {t(
                                "Please note, this application is associated with a published Template."
                            )}
                        </span>
                    </div>
                )}
            </ModalBody>
            <ModalFooter className="border-top-0">
                <CustomSecondaryColorButton
                    outline
                    variant="light"
                    onClick={() => {
                        setIsConfirmationModalArchived(
                            !isConfirmationModalArchived
                        );
                    }}
                    rounded
                >
                    {t("Cancel")}
                </CustomSecondaryColorButton>
                <StyledButton
                    rounded
                    className="cmn_btn_validateAlert mr-2"
                    variant="primary"
                    onClick={handleArchive}
                >
                    {t("Validate")}
                </StyledButton>
            </ModalFooter>
        </StyledModal>
    );
}
